/**
 * 入口の画面（ダッシュボード・決定21）。
 *
 * 7月の提案資料のモックどおり、**上に数字のカード4枚、下に左「AI先輩に教える ― 答えられなかった質問」、
 * 右「メンバー・権限」**の形にする（総点検 M4）。最初の画面で「いま何が起きていて、何をすればよいか」が
 * 分かるようにするためで、ご自身の権限の箇条書きは「役職とできること」の画面にある。
 *
 * 出し分けは「できることの束」で行う（docs/01 5-4）。名簿を管理できない方には名簿の数字を、
 * AI先輩の答えを承認できない方には質問の数字を出さない（窓口も呼ばない）。
 * 受け持ちが自拠点の方には、自拠点の分だけが出る（サーバが決める・docs/01 15-1）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/context.js';
import type {
  AiUnansweredPage,
  DashboardAiCounts,
  DashboardMemberCounts,
  MemberPage,
} from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { summarize } from '../ai/labels.js';
import { MemberStatusTag, RoleTag } from '../components/MemberTags.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { Card } from '../components/ui/Card.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { formatDateTime } from '../format/datetime.js';
import { can } from '../menu/visible.js';

/** 「答えられなかった質問」に並べる件数（モックと同じ3件）。 */
const QUESTIONS_SHOWN = 3;
/** 答えを作り始めた質問を除いたあとに3件残るよう、少し多めに読む。 */
const QUESTIONS_READ = 20;
/** 「メンバー・権限」に並べる人数。 */
const MEMBERS_SHOWN = 5;

interface Loaded<T> {
  data: T | null;
  failure: LoadFailure | null;
}

/** 1つの窓口を読む（読めなかったときも画面全体は止めず、その囲みだけで理由を出す）。 */
function useLoad<T>(enabled: boolean, load: () => Promise<T>, key: string): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ data: null, failure: null });
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState({ data: null, failure: null });
    load()
      .then((data) => {
        if (!cancelled) setState({ data, failure: null });
      })
      .catch((caught: unknown) => {
        if (!cancelled) setState({ data: null, failure: loadFailure(caught) });
      });
    return () => {
      cancelled = true;
    };
    // 読み直すきっかけは key だけ（load は毎回新しく作られるため）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);
  return state;
}

export function DashboardPage(): ReactNode {
  const { profile } = useSession();
  const api = useApi();
  const grant = profile?.grant;
  const canMembers = grant ? can(grant, 'member.manage') : false;
  const canAi = grant ? can(grant, 'ai_answer.approve') : false;
  const canImport = grant ? can(grant, 'roster.manage') : false;
  const canSupport = grant ? can(grant, 'channel.manage_any') : false;
  const [reloadKey, setReloadKey] = useState(0);
  const key = String(reloadKey);

  const memberCounts = useLoad(
    canMembers,
    () => api.get<DashboardMemberCounts>('/admin/dashboard/members'),
    key,
  );
  const members = useLoad(
    canMembers,
    () => api.get<MemberPage>('/admin/members', { query: { limit: MEMBERS_SHOWN } }),
    key,
  );
  const aiCounts = useLoad(canAi, () => api.get<DashboardAiCounts>('/admin/dashboard/ai'), key);
  const supportCounts = useLoad(
    canSupport,
    () => api.get<{ people: number }>('/admin/support/inactive/count'),
    key,
  );
  const questions = useLoad(
    canAi,
    () => api.get<AiUnansweredPage>('/admin/ai/unanswered', { query: { limit: QUESTIONS_READ } }),
    key,
  );

  if (!profile) return null;
  const retry = (): void => setReloadKey((n) => n + 1);
  const scopeNote = profile.grant.scope === 'org' ? '全社' : '自拠点';

  // 答えを作り始めた質問は「承認待ち」の側で扱うので、ここでは出さない（件数の数え方と同じ）
  const openQuestions = (questions.data?.items ?? [])
    .filter((item) => (item.draft_id !== undefined ? item.draft_id === null : !item.has_draft))
    .slice(0, QUESTIONS_SHOWN);

  const stats: { label: string; value: number | null; note: string; brand?: boolean }[] = [];
  if (canMembers) {
    stats.push({
      label: '利用中のメンバー',
      value: memberCounts.data?.active ?? null,
      note: memberCounts.data ? `ログイン前の方 ${memberCounts.data.invited}名` : scopeNote,
    });
    stats.push({
      label: 'この7日間に送った方',
      value: memberCounts.data?.senders_7d ?? null,
      note: 'メッセージを1通以上送った方',
    });
  }
  if (canAi) {
    stats.push({
      label: '答えられなかった質問',
      value: aiCounts.data?.unanswered ?? null,
      note: aiCounts.data
        ? `承認待ちの答え${profile.grant.scope === 'org' ? '' : '（全社）'}${aiCounts.data.pending}件`
        : 'AI先輩が答えられなかった質問',
      brand: (aiCounts.data?.unanswered ?? 0) > 0,
    });
  }
  if (canMembers) {
    stats.push({
      label: 'この7日間のメッセージ',
      value: memberCounts.data?.messages_7d ?? null,
      note: '送信取消したものは数えません',
    });
  }

  const countsFailure = memberCounts.failure ?? aiCounts.failure ?? supportCounts.failure;
  // 参加の申し込み（承認待ち）の件数（決定34）。古いサーバで項目が無いときは出さない
  const joinPending = memberCounts.data?.join_requests_pending;
  // ログインの上限（本部管理者だけに届く・#336）。当たったら帯で知らせる
  const loginLimit = memberCounts.data?.login_limit;
  // ストアの審査用の入口が開いている（本部管理者だけに届く・#536）。開けたままにしないように
  const storeReviewOpen = memberCounts.data?.store_review_login?.enabled === true;
  const storeReviewCollision = memberCounts.data?.store_review_login?.phone_collision === true;

  return (
    <article>
      <PageHeader title="ダッシュボード" description={`いまの様子です（${scopeNote}の分）。`} />

      {countsFailure ? <ErrorBlock failure={countsFailure} onRetry={retry} /> : null}

      {/* 審査用の入口の状態。本部管理者だけに出る（#536・#563 O-3） */}
      {storeReviewCollision ? (
        <ResultBanner
          tone="error"
          message="審査用の電話番号がメンバーの電話番号と重なっているため、審査用の入口を閉じています。"
          nextStep="メンバーの方は、ふつうのログイン（SMS）をお使いいただけます。サーバを管理する方に、審査用の電話番号をメンバーに登録されていない番号へ変えるようご依頼ください。"
        />
      ) : storeReviewOpen ? (
        <ResultBanner
          tone="info"
          message="いま、アプリの審査用の入口が開いています（ストアの審査の期間だけ使う入口です）。"
          nextStep="この入口から入れるのは審査用の会社だけで、この会社のメンバーの情報やトークは見られません。審査が終わったら、サーバの設定で閉じます。"
        />
      ) : null}

      {canSupport && supportCounts.data && supportCounts.data.people > 0 ? (
        <ResultBanner
          tone="info"
          message={`しばらく開いていない応援の方が${supportCounts.data.people}名います`}
          action={
            <Link
              className="ui-button ui-button-secondary ui-button-compact"
              to="/support/inactive"
            >
              応援の方の一覧を開く
            </Link>
          }
        />
      ) : null}

      {/* ログインの上限に当たった（この24時間）。本部管理者だけに出る（#336） */}
      {loginLimit?.reached ? (
        <ResultBanner
          tone="error"
          // 見出しの名前も「（24時間）」で終わるので、日時を先に置いて括弧を続けない（#411 L-5）
          message={`ログインの上限に達しました（${formatDateTime(loginLimit.reached.at)}）：${loginLimit.reached.label}。`}
          nextStep={
            loginLimit.raise
              ? 'しばらくのあいだ、新しくログインできない方がいたかもしれません（すでにログインしている方は、そのまま使えます）。いまは上限を引き上げています。心当たりが無いときは、大量に送らせようとされている可能性があります。'
              : 'しばらくのあいだ、新しくログインできない方がいます（すでにログインしている方は、そのまま使えます）。導入の日などで一斉に入っていただいている場合は、上限を一時的に引き上げてください。心当たりが無いときは、大量に送らせようとされている可能性があります。'
          }
          action={
            // 主役の赤いボタンは画面に1つだけ（#334 L-8）。帯そのものが赤いので、ボタンは控えめに
            <Link className="ui-button ui-button-secondary ui-button-compact" to="/login-limits">
              ログインの上限を開く
            </Link>
          }
        />
      ) : null}
      {loginLimit?.raise ? (
        <ResultBanner
          tone="info"
          message={`いま、ログインの上限を${loginLimit.raise.factor}倍に引き上げています（${formatDateTime(loginLimit.raise.ends_at)}まで）。`}
          nextStep="時間がたつと、自動でいつもの上限に戻ります。"
          // 上の帯に同じリンクがあるときは重ねて出さない
          {...(loginLimit.reached
            ? {}
            : {
                action: (
                  <Link
                    className="ui-button ui-button-secondary ui-button-compact"
                    to="/login-limits"
                  >
                    ログインの上限を開く
                  </Link>
                ),
              })}
        />
      ) : null}

      {/* 承認を待っている申し込みがあるときだけ、いちばん上で知らせる（0件のときは下の囲みに件数を出す） */}
      {canMembers && joinPending !== undefined && joinPending > 0 ? (
        <ResultBanner
          tone="info"
          message={`参加の申し込み（承認待ち）が${joinPending.toLocaleString('ja-JP')}件あります。`}
          nextStep="ご本人か確かめてから、承認してください。"
          action={
            <Link className="ui-button ui-button-primary ui-button-compact" to="/join-requests">
              参加の申し込みを開く
            </Link>
          }
        />
      ) : null}

      {stats.length > 0 ? (
        <ul className="admin-stats" aria-label="いまの数字">
          {stats.map((stat) => (
            <li key={stat.label} className="admin-stat">
              <span className="admin-stat-label">{stat.label}</span>
              <span className={`admin-stat-value${stat.brand ? ' is-brand' : ''}`}>
                {stat.value === null ? '―' : stat.value.toLocaleString('ja-JP')}
              </span>
              <span className="admin-stat-note">{stat.note}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="admin-dashboard-panels">
        {canAi ? (
          <Card
            title="AI先輩に教える ― 答えられなかった質問"
            description="答えを書いて承認すると、次からAI先輩が答えられるようになります。"
          >
            {questions.failure ? <ErrorBlock failure={questions.failure} onRetry={retry} /> : null}
            {!questions.failure && !questions.data ? <LoadingBlock /> : null}
            {questions.data && openQuestions.length === 0 ? (
              <p className="admin-empty">いま答えを待っている質問はありません。</p>
            ) : null}
            {openQuestions.length > 0 ? (
              <ul className="admin-rows">
                {openQuestions.map((item) => (
                  <li key={item.id}>
                    <div className="admin-rows-main">{summarize(item.question, 50)}</div>
                    <div className="admin-rows-side">
                      <Link
                        className="ui-button ui-button-primary ui-button-compact"
                        to={`/ai/questions/${item.id}`}
                        state={{ from: '/ai/unanswered' }}
                      >
                        答えを書く
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="admin-actions">
              <Link className="ui-button ui-button-secondary ui-button-compact" to="/ai/unanswered">
                答えられなかった質問をすべて見る
              </Link>
              <Link className="ui-button ui-button-secondary ui-button-compact" to="/ai/pending">
                承認待ちを見る
                {aiCounts.data && aiCounts.data.pending > 0 ? `（${aiCounts.data.pending}件）` : ''}
              </Link>
            </div>
          </Card>
        ) : null}

        {canMembers ? (
          <Card
            title="メンバー・権限"
            description="役職と在籍状態です。辞めた方は、その方を開いてすぐに無効にできます。"
          >
            {members.failure ? <ErrorBlock failure={members.failure} onRetry={retry} /> : null}
            {!members.failure && !members.data ? <LoadingBlock /> : null}
            {members.data && members.data.items.length === 0 ? (
              <p className="admin-empty">まだメンバーが登録されていません。</p>
            ) : null}
            {members.data && members.data.items.length > 0 ? (
              <ul className="admin-rows">
                {members.data.items.map((member) => (
                  <li key={member.id}>
                    <div className="admin-rows-main">
                      <Link to={`/members/${member.id}`}>{member.display_name}</Link>
                      {member.site_name ? <small>{member.site_name}</small> : null}
                    </div>
                    <div className="admin-rows-side admin-tags">
                      <RoleTag label={member.role_display_name} />
                      <MemberStatusTag status={member.status} />
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="admin-actions">
              {/* 主役の赤いボタンは画面に1つだけ（左の「答えを書く」・#334 L-8） */}
              <Link className="ui-button ui-button-secondary ui-button-compact" to="/members">
                メンバーの一覧を開く
              </Link>
              <Link className="ui-button ui-button-secondary ui-button-compact" to="/join-requests">
                参加の申し込み
                {joinPending !== undefined ? `（承認待ち${joinPending}件）` : ''}
              </Link>
              {canImport ? (
                <Link
                  className="ui-button ui-button-secondary ui-button-compact"
                  to="/roster/import"
                >
                  CSVでまとめて登録
                </Link>
              ) : null}
            </div>
          </Card>
        ) : null}
      </div>

      {!canAi && !canMembers ? (
        <p className="admin-empty">左の一覧から、お使いになる画面を選んでください。</p>
      ) : null}
    </article>
  );
}
